import { Box, Button, Typography } from '@mui/material'
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined'

/**
 * What a surface shows when a read or a write failed.
 *
 * An error that only says something went wrong costs the reader a support
 * message. Three things make the difference: what failed, whether anything
 * was changed, and how to try again. `detail` carries the status and route
 * so a report can be made without reproducing the failure first.
 *
 * `onRetry` is omitted when retrying is not safe — a write that may have
 * half-applied gets a `null` retry and a description saying so, rather than a
 * button that quietly submits twice.
 */
export default function ErrorState({
  title,
  description,
  detail,
  onRetry,
  dense,
}: {
  title: string
  description?: React.ReactNode
  detail?: string
  onRetry?: () => void
  dense?: boolean
}) {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        px: 3,
        py: dense ? 4 : 7,
      }}
    >
      <Box
        sx={{
          width: 52,
          height: 52,
          mb: 1.75,
          borderRadius: '12px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'error.dark',
          bgcolor: 'background.paper',
          border: 1,
          borderColor: 'error.main',
          '& svg': { fontSize: 26 },
        }}
      >
        <ReportProblemOutlinedIcon />
      </Box>
      <Typography variant="subtitle2" component="p" sx={{ fontSize: '0.875rem' }}>
        {title}
      </Typography>
      {description && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mt: 0.75, maxWidth: 380 }}
        >
          {description}
        </Typography>
      )}
      {detail && (
        <Box
          sx={{
            mt: 1.5,
            px: 1.25,
            py: 1,
            maxWidth: 380,
            width: '100%',
            borderRadius: '6px',
            border: 1,
            borderColor: 'divider',
            bgcolor: 'background.default',
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
            fontSize: '0.6875rem',
            color: 'text.secondary',
            textAlign: 'left',
            wordBreak: 'break-all',
          }}
        >
          {detail}
        </Box>
      )}
      {onRetry && (
        <Button variant="contained" size="small" onClick={onRetry} sx={{ mt: 2 }}>
          Try again
        </Button>
      )}
    </Box>
  )
}
